import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import { Fragment, useState } from 'react';
import { useTableControls } from '../hooks/useTableControls';
import type { Column } from '../hooks/useTableControls';

export function ToilPage() {
  const { data: categoriesData, isLoading: catLoading, error: catError, refetch: refetchCats } = useQuery({
    queryKey: ['toil-categories'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/toil/categories');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: candidatesData, isLoading: candLoading, error: candError, refetch: refetchCands } = useQuery({
    queryKey: ['toil-automation-candidates'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/toil/automation-candidates');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const categories = categoriesData?.categories || [];
  const candidates = candidatesData?.items || [];
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // GET /categories only returns the summary row (id/label/volume/hours/trend)
  // — representative_tickets/affected_cis/assignment_groups live on the
  // per-category detail endpoint and were never fetched, so the expand panel
  // always rendered empty regardless of which row was clicked.
  const { data: categoryDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['toil-category-detail', expandedRow],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/toil/categories/${expandedRow}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!expandedRow,
  });

  const totalHours = categories.reduce((sum: number, c: any) => sum + (c.total_hours || 0), 0);
  const totalVolume = categories.reduce((sum: number, c: any) => sum + (c.ticket_volume || 0), 0);

  const sortedCandidates = [...candidates].sort((a: any, b: any) => {
    const scoreA = (a.hours_reclaimable || 0) * (a.feasibility_score || 1);
    const scoreB = (b.hours_reclaimable || 0) * (b.feasibility_score || 1);
    return scoreB - scoreA;
  });

  const topCandidate = sortedCandidates[0];
  const estimatedEffort = topCandidate?.estimated_effort_hours || 0;
  const paybackMonths = estimatedEffort > 0 && topCandidate?.hours_reclaimable
    ? Math.ceil(estimatedEffort / topCandidate.hours_reclaimable * 12)
    : null;

  const isLoading = catLoading || candLoading;
  const hasError = catError || candError;

  // Category table columns
  const categoryColumns: Column<any>[] = [
    { key: 'cluster_label', label: 'Cluster Label' },
    { key: 'ticket_volume', label: 'Ticket Volume', accessor: (row: any) => row.ticket_volume },
    { key: 'median_handle_time', label: 'Median Handle Time' },
    { key: 'total_hours', label: 'Total Hours', accessor: (row: any) => row.total_hours },
    { key: 'trend', label: 'Trend' },
  ];

  // Automation candidate columns
  const candidateColumns: Column<any>[] = [
    { key: '_rank', label: '#', sortable: false, render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row._rank}</span> },
    { key: 'category', label: 'Category' },
    { key: 'hours_reclaimable', label: 'Hours Reclaimable / Year', render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row.hours_reclaimable?.toLocaleString() ?? '—'}</span> },
    { key: 'feasibility_score', label: 'Feasibility (1–5)', render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row.feasibility_score ?? '—'}</span> },
    {
      key: 'composite_score',
      label: 'Composite Score',
      accessor: (row: any) => row.hours_reclaimable != null && row.feasibility_score != null ? row.hours_reclaimable * row.feasibility_score : null,
      render: (row: any) => (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
          {row.hours_reclaimable != null && row.feasibility_score != null
            ? (row.hours_reclaimable * row.feasibility_score).toLocaleString()
            : '—'}
        </span>
      ),
    },
  ];

  const categoriesTable = useTableControls(categories, categoryColumns, { storageKey: 'toil-categories' });
  const rankedCandidates = sortedCandidates.map((c: any, i: number) => ({ ...c, _rank: i + 1 }));
  const candidatesTable = useTableControls(rankedCandidates, candidateColumns, {
    defaultSortKey: 'composite_score',
    defaultSortDir: 'desc',
    storageKey: 'toil-candidates',
  });

  // Saved views state for categories
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    categoriesTable.saveView(newViewName.trim());
    setNewViewName('');
    setShowSavedViews(false);
  };

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Operations</p>
              <h1 className="pagehead__title">Toil &amp; Ticket Taxonomy</h1>
              <div className="pagehead__facts">
                <span className="fact">Recurring ticket clusters &amp; automation candidates</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading toil data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Operations</p>
              <h1 className="pagehead__title">Toil &amp; Ticket Taxonomy</h1>
              <div className="pagehead__facts">
                <span className="fact">Recurring ticket clusters &amp; automation candidates</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load toil data</h3>
                <p className="state__body">
                  Could not connect to the toil API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => { refetchCats(); refetchCands(); }}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Operations</p>
            <h1 className="pagehead__title">Toil &amp; Ticket Taxonomy</h1>
            <div className="pagehead__facts">
              <span className="fact">Recurring ticket clusters, automation candidates, and business case</span>
              <span className="fact">Categories: <b>{categories.length}</b></span>
              <span className="fact">Candidates: <b>{candidates.length}</b></span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Summary Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Toil summary">
          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Volume</p>
              <p className="ind__full">Total Ticket Volume</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{totalVolume.toLocaleString()}</p>
            </div>
            <p className="ind__reading">
              <i></i> <b>Across</b> — all identified clusters
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Hours</p>
              <p className="ind__full">Annual Hours Consumed</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{totalHours.toLocaleString()}</p>
            </div>
            <p className="ind__reading">
              <i></i> <b>Consumed</b> — by recurring manual work
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Clusters</p>
              <p className="ind__full">Categories Identified</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{categories.length}</p>
            </div>
            <p className="ind__reading">
              <i></i> <b>Classified</b> — recurring ticket patterns
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Candidates</p>
              <p className="ind__full">Automation Candidates</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{candidates.length}</p>
            </div>
            <p className="ind__reading">
              <i></i> <b>Ready</b> — ranked by composite score
            </p>
          </div>
        </div>

        {/* Ticket Clusters Table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Ticket Clusters</p>
              <h2 className="box__title">Recurring Patterns</h2>
            </div>
            <div className="box__head-actions">
              <button className="btn btn--sm" onClick={categoriesTable.exportCSV} title="Export CSV">CSV</button>
              <button className="btn btn--sm" onClick={categoriesTable.exportExcel} title="Export JSON">JSON</button>
            </div>
          </div>
          <div className="box__body">
            {/* Search and controls */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end', marginBottom: 'var(--sp-4)' }}>
              <div className="field" style={{ flex: '1 1 250px' }}>
                <label>Search</label>
                <input
                  type="text"
                  value={categoriesTable.search}
                  onChange={e => categoriesTable.setSearch(e.target.value)}
                  placeholder="Search clusters..."
                />
              </div>
              <div className="field">
                <label>Rows</label>
                <select
                  value={categoriesTable.pageSize}
                  onChange={e => { categoriesTable.setPageSize(Number(e.target.value)); categoriesTable.setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {/* Column visibility */}
              <div className="field" style={{ position: 'relative' }}>
                <label>Columns</label>
                <button
                  className="btn btn--sm"
                  onClick={() => setShowColumnPicker(!showColumnPicker)}
                >
                  ☰ {categoriesTable.visibleColumns.length}/{categoryColumns.length}
                </button>
                {showColumnPicker && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, zIndex: 50,
                    background: 'var(--surface)', border: '1px solid var(--hair)',
                    borderRadius: 'var(--r-box)', padding: 'var(--sp-3)', minWidth: 200,
                    boxShadow: 'var(--sh-pop)',
                  }}>
                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-45)' }}>Toggle Columns</div>
                    {categoryColumns.filter(c => c.hideable !== false).map(col => (
                      <label key={col.key} className="cbx" style={{ padding: 'var(--sp-1) 0', fontSize: 'var(--text-sm)' }}>
                        <input
                          type="checkbox"
                          checked={!categoriesTable.hiddenColumns.includes(col.key)}
                          onChange={() => categoriesTable.toggleColumn(col.key)}
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {/* Saved views */}
              <div className="field" style={{ position: 'relative' }}>
                <label>Views</label>
                <button
                  className="btn btn--sm"
                  onClick={() => setShowSavedViews(!showSavedViews)}
                >
                  ★ Views {categoriesTable.savedViews.length > 0 && `(${categoriesTable.savedViews.length})`}
                </button>
                {showSavedViews && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, zIndex: 50,
                    background: 'var(--surface)', border: '1px solid var(--hair)',
                    borderRadius: 'var(--r-box)', padding: 'var(--sp-3)', minWidth: 240,
                    boxShadow: 'var(--sh-pop)',
                  }}>
                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-45)' }}>Save Current View</div>
                    <div style={{ display: 'flex', gap: 'var(--sp-1)', marginBottom: 'var(--sp-3)' }}>
                      <input
                        type="text"
                        value={newViewName}
                        onChange={e => setNewViewName(e.target.value)}
                        placeholder="View name..."
                        className="input"
                        style={{ flex: 1 }}
                        onKeyDown={e => e.key === 'Enter' && handleSaveView()}
                      />
                      <button className="btn btn--primary btn--sm" onClick={handleSaveView}>
                        Save
                      </button>
                    </div>
                    {categoriesTable.savedViews.length > 0 && (
                      <>
                        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: 'var(--sp-1)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-45)' }}>Saved Views</div>
                        {categoriesTable.savedViews.map(v => (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--sp-1) 0' }}>
                            <button
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--gold)', textAlign: 'left' }}
                              onClick={() => { categoriesTable.loadView(v.id); setShowSavedViews(false); }}
                            >
                              {v.name}
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              onClick={() => categoriesTable.deleteView(v.id)}
                              style={{ padding: 'var(--sp-1)' }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              {(categoriesTable.search || Object.keys(categoriesTable.activeFilters).length > 0) && (
                <button className="btn btn--ghost btn--sm" onClick={categoriesTable.clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)', marginBottom: 'var(--sp-2)' }}>
              Showing {categoriesTable.paged.length} of {categoriesTable.totalFiltered} results
            </div>

            {categoriesTable.paged.length === 0 ? (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <h3 className="state__title">No toil categories found</h3>
                <p className="state__body">
                  {categoriesTable.search || Object.keys(categoriesTable.activeFilters).length > 0
                    ? 'Try adjusting your search or filters.'
                    : 'No toil categories have been identified yet.'}
                </p>
              </div>
            ) : (
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 24 }}></th>
                      {categoriesTable.visibleColumns.map(col => (
                        <th key={col.key}>
                          <button
                            className={`th${col.accessor ? ' th--num' : ''}`}
                            onClick={() => categoriesTable.setSort(col.key)}
                            aria-sort={categoriesTable.sortKey === col.key ? (categoriesTable.sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                          >
                            {col.label}
                            {categoriesTable.sortKey === col.key && (
                              <span style={{ marginLeft: 4 }}>
                                {categoriesTable.sortDir === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categoriesTable.paged.map((c: any) => (
                      <Fragment key={c.id}>
                        <tr
                          key={c.id}
                          onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{expandedRow === c.id ? '▾' : '▸'}</td>
                          {categoriesTable.visibleColumns.map(col => (
                            <td key={col.key}>
                              {col.key === 'ticket_volume' && <span className="td--num">{c.ticket_volume?.toLocaleString() ?? '—'}</span>}
                              {col.key === 'median_handle_time' && <span className="td--num">{c.median_handle_time || '—'}</span>}
                              {col.key === 'total_hours' && <span className="td--num">{c.total_hours?.toLocaleString() ?? '—'}</span>}
                              {col.key === 'cluster_label' && <span className="td--name">{c.cluster_label}</span>}
                              {col.key === 'trend' && (
                                <span className={`pill pill--${c.trend === 'up' ? 'risk' : c.trend === 'down' ? 'ok' : 'warn'}`}>
                                  <i></i>
                                  {c.trend === 'up' ? '↑ Rising' : c.trend === 'down' ? '↓ Falling' : '→ Stable'}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                        {expandedRow === c.id && (
                          <tr key={`${c.id}-detail`}>
                            <td colSpan={categoriesTable.visibleColumns.length + 1} style={{ padding: 'var(--sp-3)', background: 'var(--surface-sub)' }}>
                              {detailLoading && (
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>Loading…</span>
                              )}
                              {!detailLoading && categoryDetail?.representative_tickets && categoryDetail.representative_tickets.length > 0 && (
                                <div style={{ marginBottom: 'var(--sp-4)' }}>
                                  <strong style={{ fontSize: 'var(--text-sm)' }}>Representative Tickets</strong>
                                  <ul style={{ margin: 'var(--sp-1) 0', paddingLeft: 'var(--sp-4)' }}>
                                    {categoryDetail.representative_tickets.map((t: any, i: number) => (
                                      <li key={i} style={{ fontSize: 'var(--text-sm)' }}>{t.title || t}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {!detailLoading && categoryDetail?.affected_cis && categoryDetail.affected_cis.length > 0 && (
                                <div style={{ marginBottom: 'var(--sp-4)' }}>
                                  <strong style={{ fontSize: 'var(--text-sm)' }}>Affected CIs</strong>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
                                    {categoryDetail.affected_cis.map((ci: any, i: number) => (
                                      <span key={i} className="pill"><i></i>{ci.name || ci}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {!detailLoading && categoryDetail?.assignment_groups && categoryDetail.assignment_groups.length > 0 && (
                                <div>
                                  <strong style={{ fontSize: 'var(--text-sm)' }}>Assignment Groups</strong>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
                                    {categoryDetail.assignment_groups.map((g: string, i: number) => (
                                      <span key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{g}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {!detailLoading && categoryDetail && !categoryDetail.representative_tickets?.length && !categoryDetail.affected_cis?.length && !categoryDetail.assignment_groups?.length && (
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>No further detail for this category.</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {categoriesTable.totalPages > 1 && (
              <div className="box__foot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--sp-3)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                  Page {categoriesTable.page} of {categoriesTable.totalPages}
                </span>
                <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                  <button className="btn btn--sm" disabled={categoriesTable.page <= 1} onClick={() => categoriesTable.setPage(1)}>⟪</button>
                  <button className="btn btn--sm" disabled={categoriesTable.page <= 1} onClick={() => categoriesTable.setPage(categoriesTable.page - 1)}>← Prev</button>
                  <button className="btn btn--sm" disabled={categoriesTable.page >= categoriesTable.totalPages} onClick={() => categoriesTable.setPage(categoriesTable.page + 1)}>Next →</button>
                  <button className="btn btn--sm" disabled={categoriesTable.page >= categoriesTable.totalPages} onClick={() => categoriesTable.setPage(categoriesTable.totalPages)}>⟫</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Automation Candidate Ranking */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Automation</p>
              <h2 className="box__title">Candidate Ranking</h2>
            </div>
            <div className="box__head-actions">
              <button className="btn btn--sm" onClick={candidatesTable.exportCSV} title="Export CSV">CSV</button>
              <button className="btn btn--sm" onClick={candidatesTable.exportExcel} title="Export JSON">JSON</button>
            </div>
          </div>
          <div className="box__body">
            {/* Search */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end', marginBottom: 'var(--sp-4)' }}>
              <div className="field" style={{ flex: '1 1 250px' }}>
                <label>Search</label>
                <input
                  type="text"
                  value={candidatesTable.search}
                  onChange={e => candidatesTable.setSearch(e.target.value)}
                  placeholder="Search candidates..."
                />
              </div>
              <div className="field">
                <label>Rows</label>
                <select
                  value={candidatesTable.pageSize}
                  onChange={e => { candidatesTable.setPageSize(Number(e.target.value)); candidatesTable.setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {(candidatesTable.search || Object.keys(candidatesTable.activeFilters).length > 0) && (
                <button className="btn btn--ghost btn--sm" onClick={candidatesTable.clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)', marginBottom: 'var(--sp-2)' }}>
              Showing {candidatesTable.paged.length} of {candidatesTable.totalFiltered} results
            </div>

            {candidatesTable.paged.length === 0 ? (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <h3 className="state__title">No automation candidates found</h3>
                <p className="state__body">
                  {candidatesTable.search
                    ? 'Try adjusting your search.'
                    : 'No automation candidates have been identified yet.'}
                </p>
              </div>
            ) : (
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      {candidatesTable.visibleColumns.map(col => (
                        <th key={col.key}>
                          <button
                            className={`th${col.accessor ? ' th--num' : ''}`}
                            onClick={() => candidatesTable.setSort(col.key)}
                            aria-sort={candidatesTable.sortKey === col.key ? (candidatesTable.sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                          >
                            {col.label}
                            {candidatesTable.sortKey === col.key && (
                              <span style={{ marginLeft: 4 }}>
                                {candidatesTable.sortDir === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {candidatesTable.paged.map((c: any) => (
                      <tr key={c.category}>
                        {candidatesTable.visibleColumns.map(col => (
                          <td key={col.key}>
                            {col.key === '_rank' && <span className="td--num">{c._rank}</span>}
                            {col.key === 'category' && <span className="td--name">{c.category}</span>}
                            {col.key === 'hours_reclaimable' && <span className="td--num">{c.hours_reclaimable?.toLocaleString() ?? '—'}</span>}
                            {col.key === 'feasibility_score' && <span className="td--num">{c.feasibility_score ?? '—'}</span>}
                            {col.key === 'composite_score' && (
                              <span className="td--num">
                                {c.hours_reclaimable != null && c.feasibility_score != null
                                  ? (c.hours_reclaimable * c.feasibility_score).toLocaleString()
                                  : '—'}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {candidatesTable.totalPages > 1 && (
              <div className="box__foot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--sp-3)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                  Page {candidatesTable.page} of {candidatesTable.totalPages}
                </span>
                <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                  <button className="btn btn--sm" disabled={candidatesTable.page <= 1} onClick={() => candidatesTable.setPage(1)}>⟪</button>
                  <button className="btn btn--sm" disabled={candidatesTable.page <= 1} onClick={() => candidatesTable.setPage(candidatesTable.page - 1)}>← Prev</button>
                  <button className="btn btn--sm" disabled={candidatesTable.page >= candidatesTable.totalPages} onClick={() => candidatesTable.setPage(candidatesTable.page + 1)}>Next →</button>
                  <button className="btn btn--sm" disabled={candidatesTable.page >= candidatesTable.totalPages} onClick={() => candidatesTable.setPage(candidatesTable.totalPages)}>⟫</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Business Case Summary */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Business Case</p>
              <h2 className="box__title">Automation ROI</h2>
            </div>
          </div>
          <div className="box__body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }}>
              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Current</p>
                  <p className="ind__full">Annual Hours Consumed</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{totalHours.toLocaleString()}</p>
                </div>
                <p className="ind__reading">
                  <i></i> <b>Baseline</b> — current manual effort
                </p>
              </div>

              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Effort</p>
                  <p className="ind__full">Estimated Automation Effort</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{estimatedEffort ? `${estimatedEffort}h` : '—'}</p>
                </div>
                <p className="ind__reading">
                  <i></i> <b>Investment</b> — one-time automation cost
                </p>
              </div>

              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Payback</p>
                  <p className="ind__full">Payback Period</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{paybackMonths != null ? `${paybackMonths} mo` : '—'}</p>
                </div>
                <p className="ind__reading">
                  <i></i> <b>Return</b> — time to recover investment
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
